import { RequestConfig } from '../types'
import { AxiosAPI } from './AxiosApi'

class SippApiClass extends AxiosAPI {
  public fetch = {
    userByIdOrEmail: async (id: string, options: RequestConfig = {}) =>
      this.GET(this.apiSchema.GET.users.user(id), options),
    waterTestKitRequestsByUserIdOrEmail: async (id: string, options: RequestConfig = {}) =>
      this.GET(this.apiSchema.GET.users.testKitRequests(id), options),
    waterTestKitRequests: async (options: RequestConfig = {}) =>
      this.GET(this.apiSchema.GET.waterTestKitRequests.waterTestKitRequests(), options),
    waterTestKitResults: async (options: RequestConfig = {}) =>
      this.GET(this.apiSchema.GET.waterTestStrips.analysis(), options),
    waterTestKitResultById: async (id: string, options: RequestConfig = {}) =>
      this.GET(this.apiSchema.GET.waterTestStrips.analysisById(id), options),
    waterTestKitResultByUserIdOrEmail: async (id: string, options: RequestConfig = {}) =>
      this.GET(this.apiSchema.GET.users.testKitResults(id), options),
    urlMapByCode: async (code: string, options: RequestConfig) => {
      return await this.GET(this.apiSchema.GET.urlMap(code))
    },
  }
  public create = {
    waterTestStripManualAnalyze: async (body: any, options = {}) => {
      return this.POST(this.apiSchema.POST.waterTestStrips.manualAnalyze(), body, options)
    },
    waterTestStripRequest: async (data: any, options = {}) => {
      return await this.POST(
        this.apiSchema.POST.waterTestStrips.waterTestStripRequest(),
        data,
        options,
      )
    },
    otp: async (email: string, options = {}) => {
      return await this.POST(this.apiSchema.POST.verification.createOtp(), { email }, options)
    },
    otpVerify: async (email: string, otp: string, options = {}) => {
      return await this.POST(this.apiSchema.POST.verification.verifyOtp(), { email, otp }, options)
    },
    urlMap: async (body: { url: string; prefix?: string }, options: RequestConfig) => {
      return await this.POST(this.apiSchema.POST.urlMaps(), body, options)
    },
    shareResults: async (body: { recipientEmail: string; senderName: string; shareUrl: string; message?: string }, options = {}) => {
      return await this.POST(this.apiSchema.POST.share.results(), body, options)
    },
    generateShareToken: async (options = {}) => {
      return await this.POST(this.apiSchema.POST.share.token(), {}, options)
    },
    // SIPP-611: fire WQI Report Completed event to Klaviyo + Customer.io
    wqiReportCompleted: async (
      userId: string,
      body: { wqiScore: number; wqiBand: string; topParameter: string; utilityName: string },
      options = {},
    ) => {
      return this.POST(this.apiSchema.POST.users.wqiReportCompleted(userId), body, options)
    },
    // SIPP-807/808: look up Earthsmart filter code for a refrigerator brand + model.
    // Backend persists the capture on the authed user on 200.
    matchRefrigerator: async (body: { brand: string; model: string }, options = {}) => {
      return this.POST(this.apiSchema.POST.equipment.refrigeratorMatch(), body, options)
    },
    // SIPP-809 / SIPP-812: record a product-waitlist signup. Body accepts
    // either `{ productType }` (D3c — backend maps to attribute) or
    // `{ attribute }` (D5 — engine already emitted the attribute name, backend
    // whitelists it). Exactly one is required; backend 400s otherwise.
    waitlistSignup: async (
      userId: string,
      body: { productType: string } | { attribute: string },
      options = {},
    ) => {
      return this.POST(this.apiSchema.POST.users.waitlistSignup(userId), body, options)
    },
    // SIPP-844: fire the free_plan_selected event to Customer.io. Body
    // carries the WQI score and safety status so segmentation can target
    // unconverted free-plan users by water quality.
    freePlanSelected: async (
      userId: string,
      body: { wqiScore: number | null; waterSafetyStatus: string },
      options = {},
    ) => {
      return this.POST(this.apiSchema.POST.users.freePlanSelected(userId), body, options)
    },
  }
  public update = {
    waterTestKitRequest: async (id: string, body: any, options: RequestConfig = {}) =>
      this.PUT(this.apiSchema.PUT.waterTestKitRequests.byId(id), body, options),
    // TODO: remove when v1 strips are fully phased out // SIPP-642
    user: async (id: string, body: any, options: RequestConfig = {}) =>
      this.PUT(this.apiSchema.PUT.users.byId(id), body, options),
  }
  public fetchUser = async (id: string, options: RequestConfig = {}) =>
    this.GET(this.apiSchema.GET.users.user(id), options)
  public fetchUserWaterTestKitRequests = async (id: string, options: RequestConfig = {}) =>
    this.GET(this.apiSchema.GET.users.testKitRequests(id), options)
  public fetchWaterTestKitRequests = async (options: RequestConfig = {}) =>
    this.GET(this.apiSchema.GET.waterTestKitRequests.waterTestKitRequests(), options)
  public fetchWaterTestKitRequestsById = async (id: string, options: RequestConfig = {}) =>
    this.GET(this.apiSchema.GET.users.testKitRequests(id), options)
  public fetchWaterTestStripAnalysis = async (options: RequestConfig = {}) =>
    this.GET(this.apiSchema.GET.waterTestStrips.analysis(), options)
  public fetchWaterTestStripAnalysisById = async (id: string, options: RequestConfig = {}) =>
    this.GET(this.apiSchema.GET.waterTestStrips.analysisById(id), options)
  public manualAnalyzeWaterTestStrip = async (body: any, options = {}) => {
    try {
      const response = await this.POST(
        this.apiSchema.POST.waterTestStrips.manualAnalyze(),
        body,
        options,
      )
      return response
    } catch (error) {
      throw error
    }
  }
  public submitWaterTestStripRequest = async (data: any, options = {}) => {
    try {
      const response = await this.POST(
        this.apiSchema.POST.waterTestStrips.waterTestStripRequest(),
        data,
        options,
      )
      return response
    } catch (error) {
      throw error
    }
  }
  public sendOtp = async (email: string, options = {}) => {
    try {
      const response = await this.POST(
        this.apiSchema.POST.verification.createOtp(),
        { email },
        options,
      )
      return response
    } catch (error) {
      throw error
    }
  }
  public verifyOtp = async (email: string, otp: string, options = {}) => {
    try {
      const response = await this.POST(
        this.apiSchema.POST.verification.verifyOtp(),
        { email, otp },
        options,
      )
      return response
    } catch (error) {
      throw error
    }
  }
  public fetchUrlMapByCode = async (code: string, options: RequestConfig) => {
    return await this.GET(this.apiSchema.GET.urlMap(code))
  }
  public createUrlMap = async (body: { url: string; prefix?: string }, options: RequestConfig) => {
    return await this.POST(this.apiSchema.POST.urlMaps(), body, options)
  }
  public getWaterQualityIndex = async (testResultId: string, options = {}) => {
    return await this.GET(
      this.apiSchema.GET.waterQualityIndex.byUserId( testResultId),
      options,
    )
  }
}

const domain =
  (window.__ENV__ &&
    window.__ENV__?.GATEWAY_API_URL !== '__GATEWAY_API_URL__' &&
    window.__ENV__?.GATEWAY_API_URL) ||
  (window.__ENV__ &&
    window.__ENV__?.SIPP_EXPRESS_API_URL !== '__SIPP_EXPRESS_API_URL__' &&
    window.__ENV__?.SIPP_EXPRESS_API_URL) ||
  process.env.REACT_APP_SIPP_EXPRESS_API_URL ||
  ''
export const SippApi = new SippApiClass(domain)
